import type { ReactNode } from 'react';
import type { DaySummary, RevealCard, ResolvedDailyRevealMode } from '@/types/reveal';
import { mapCardDetailsToRevealCard } from '@/types/reveal';
import { useDayFixtures, findFixtureForCard } from '@/hooks/useDailyReveal';
import { canEditDaySelection } from '@/utils/dayPickEdit';
import { formatUpcomingPickDateLabel } from '@/utils/upcomingPicks';
import { isPropPickLocked } from '@/utils/propBasedDailyReveal';
import {
  getEntrySportsbookSelectionIds,
  SPORTSBOOK_BETSLIP_ENABLED,
} from '@/utils/sportsbookBetslip';
import SectionHeader from './SectionHeader';
import UpcomingPickCard from './UpcomingPickCard';

type UpcomingGamesSectionProps = {
  days: DaySummary[];
  leagueIpEnabled?: boolean;
  mode?: ResolvedDailyRevealMode | null;
  onEditDay?: (dayId: string) => void;
  nowMs?: number;
  contestSport?: string | null;
  /**
   * Prefer these display cards when the entry query / optimistic pick is ahead
   * of the game-summary `selectedCard` (current day right after pick).
   */
  pickOverrides?: Record<string, RevealCard>;
  /** Sportsbook selection ids keyed by day id (current day after pick). */
  sportsbookSelectionIdsByDay?: Record<string, readonly string[]>;
  /** Entry ids keyed by day id (celebration Lottie persistence). */
  entryIdsByDay?: Record<string, string>;
  /**
   * Rendered under the section header and above the first pick card — keeps the
   * Reveal Players / countdown CTA visible when multiple upcoming picks stack.
   */
  beforeCards?: ReactNode;
};

function UpcomingPickRow({
  day,
  leagueIpEnabled,
  isPropBased,
  onEditDay,
  nowMs,
  contestSport,
  pickOverride,
  sportsbookSelectionIds,
  entryId,
}: {
  day: DaySummary;
  leagueIpEnabled: boolean;
  isPropBased: boolean;
  onEditDay?: (dayId: string) => void;
  nowMs: number;
  contestSport?: string | null;
  pickOverride?: RevealCard;
  sportsbookSelectionIds: readonly string[];
  entryId?: string | null;
}) {
  const { data: fixturesData } = useDayFixtures(day.id, {
    refetchInterval: query => {
      if (day.status === 'open' || day.status === 'locked') return 30_000;
      const fixtures =
        (query.state.data as { fixtures?: { status: string }[] } | undefined)?.fixtures ?? [];
      const hasLive = fixtures.some(f => f.status === 'live' || f.status === 'in_progress');
      return hasLive ? 30_000 : false;
    },
  });

  const entry = day.userEntry;
  if (!entry?.selectedCard && !pickOverride) return null;

  const basePick =
    pickOverride ??
    mapCardDetailsToRevealCard(entry!.selectedCard!, leagueIpEnabled, {
      propDisplayLabel: entry?.propDisplayLabel,
      propThreshold: entry?.propThreshold,
      propOperator: entry?.propOperator,
    });

  const fixtures = fixturesData?.fixtures ?? [];
  const fixture = findFixtureForCard(basePick.teamExternalId, fixtures, basePick.team);
  const pick: RevealCard = fixture
    ? { ...basePick, awayTeam: fixture.awayTeam, homeTeam: fixture.homeTeam }
    : basePick;

  const entryStatus = entry?.status ?? (pickOverride ? 'picked' : null);
  const canEditCutoff = canEditDaySelection(day, nowMs);
  const isPickLocked = isPropBased ? isPropPickLocked(pick.prop, nowMs, fixture) : false;
  const showEdit = Boolean(onEditDay && canEditCutoff && !isPickLocked);

  const dateLabel = formatUpcomingPickDateLabel(day.gameDate, nowMs, day.categoryName);

  return (
    <UpcomingPickCard
      pick={pick}
      dateLabel={dateLabel}
      totalHits={entry?.totalHits ?? 0}
      ppdHits={entry?.ppdHits ?? 0}
      ppdStatus={entry?.ppdStatus ?? null}
      fixture={fixture}
      entryStatus={entryStatus}
      isPickCutoffReached={isPickLocked}
      onEdit={showEdit && onEditDay ? () => onEditDay(day.id) : undefined}
      leagueIpEnabled={leagueIpEnabled}
      isPropBased={isPropBased}
      propResultStatus={entry?.propResultStatus ?? null}
      propResultValue={entry?.propResultValue ?? null}
      propDisplayLabel={pick.prop?.displayLabel ?? entry?.propDisplayLabel ?? null}
      propThreshold={pick.prop?.threshold ?? entry?.propThreshold ?? null}
      propOperator={pick.prop?.operator ?? entry?.propOperator ?? null}
      entryId={entryId ?? null}
      dayId={day.id}
      contestSport={contestSport}
      sportsbookSelectionIds={SPORTSBOOK_BETSLIP_ENABLED ? sportsbookSelectionIds : []}
    />
  );
}

export default function UpcomingGamesSection({
  days,
  leagueIpEnabled = false,
  mode,
  onEditDay,
  nowMs = Date.now(),
  contestSport,
  pickOverrides,
  sportsbookSelectionIdsByDay,
  entryIdsByDay,
  beforeCards,
}: UpcomingGamesSectionProps) {
  const isPropBased = mode === 'prop_based';
  if (days.length === 0) return null;

  return (
    <section className="mb-6">
      <SectionHeader title={isPropBased ? 'Upcoming Game' : 'Upcoming Pick'} />
      {beforeCards}
      {days.map(day => (
        <UpcomingPickRow
          key={day.id}
          day={day}
          leagueIpEnabled={leagueIpEnabled}
          isPropBased={isPropBased}
          onEditDay={onEditDay}
          nowMs={nowMs}
          contestSport={contestSport}
          pickOverride={pickOverrides?.[day.id]}
          sportsbookSelectionIds={
            sportsbookSelectionIdsByDay?.[day.id] ??
            getEntrySportsbookSelectionIds(
              pickOverrides?.[day.id]
                ? [{ isSelected: true, prop: pickOverrides[day.id].prop }]
                : null
            )
          }
          entryId={entryIdsByDay?.[day.id] ?? null}
        />
      ))}
    </section>
  );
}
